// ===================================================================
// src/app/features/subscriptions/services/clinic-subscription.service.ts
// Clinic Subscription Management Service
// ===================================================================

import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, catchError, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { NotificationService } from '../../../core/services/notification.service';
import { ApiResponse, PageResponse } from '../../../core/models/api-response.model';
import { ClinicSubscriptionResponse, SubscriptionHistoryResponse, SubscriptionStatisticsResponse, SubscribeClinicRequest, UpgradeSubscriptionRequest, UpgradeResponse, DowngradeSubscriptionRequest, CancelSubscriptionRequest, CustomOverrideRequest, SubscriptionSearchCriteria, SubscriptionStatus, ApproveSubscriptionRequestRequest, RejectSubscriptionRequestRequest, ReviewSubscriptionRequestRequest, SubscriptionRequest, SubscriptionRequestRequest, SubscriptionRequestStatus, SubscriptionRequestType } from '../models/subscription.model';
import { SILENT_HTTP } from '../../../core/interceptors/http-context-tokens';


@Injectable({
  providedIn: 'root'
})
export class ClinicSubscriptionService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);

  private readonly apiUrl = `${environment.apiUrl}/admin/subscriptions`;

  // State management
  subscriptions = signal<ClinicSubscriptionResponse[]>([]);
  selectedSubscription = signal<ClinicSubscriptionResponse | null>(null);
  loading = signal(false);
  history = signal<SubscriptionHistoryResponse[]>([]);
  statistics = signal<SubscriptionStatisticsResponse | null>(null);

  private subscriptionsSubject = new BehaviorSubject<ClinicSubscriptionResponse[]>([]);
  public subscriptions$ = this.subscriptionsSubject.asObservable();

  // ===================================================================
  // LIFECYCLE OPERATIONS
  // ===================================================================

  /**
   * Subscribe a clinic to a plan
   */
  subscribeClinic(request: SubscribeClinicRequest): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<ClinicSubscriptionResponse>>(`${this.apiUrl}/subscribe`, request).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success(response.message || 'تم اشتراك العيادة بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في اشتراك العيادة');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Upgrade subscription to a higher plan
   */
  upgradeSubscription(
    subscriptionId: number,
    request: UpgradeSubscriptionRequest
  ): Observable<ApiResponse<UpgradeResponse>> {
    this.loading.set(true);
    return this.http.put<ApiResponse<UpgradeResponse>>(
      `${this.apiUrl}/${subscriptionId}/upgrade`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success(response.message || 'تم ترقية الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في ترقية الاشتراك');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Downgrade subscription to a lower plan
   */
  downgradeSubscription(
    subscriptionId: number,
    request: DowngradeSubscriptionRequest
  ): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);
    return this.http.put<ApiResponse<ClinicSubscriptionResponse>>(
      `${this.apiUrl}/${subscriptionId}/downgrade`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success(response.message || 'تم جدولة تخفيض الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في تخفيض الاشتراك');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Renew an expired or expiring subscription
   */
  renewSubscription(subscriptionId: number): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<ClinicSubscriptionResponse>>(
      `${this.apiUrl}/${subscriptionId}/renew`,
      {}
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success(response.message || 'تم تجديد الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في تجديد الاشتراك');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Cancel a subscription
   */
  cancelSubscription(
    subscriptionId: number,
    request: CancelSubscriptionRequest
  ): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);
    return this.http.put<ApiResponse<ClinicSubscriptionResponse>>(
      `${this.apiUrl}/${subscriptionId}/cancel`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success(response.message || 'تم إلغاء الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في إلغاء الاشتراك');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Suspend a subscription temporarily
   */
  suspendSubscription(subscriptionId: number, reason: string): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);
    const params = new HttpParams().set('reason', reason);
    return this.http.put<ApiResponse<ClinicSubscriptionResponse>>(
      `${this.apiUrl}/${subscriptionId}/suspend`,
      {},
      { params }
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success(response.message || 'تم تعليق الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في تعليق الاشتراك');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Reactivate a suspended subscription
   */
  reactivateSubscription(subscriptionId: number): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);
    return this.http.put<ApiResponse<ClinicSubscriptionResponse>>(
      `${this.apiUrl}/${subscriptionId}/reactivate`,
      {}
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success(response.message || 'تم إعادة تفعيل الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في إعادة تفعيل الاشتراك');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  // ===================================================================
  // CUSTOM OVERRIDES (VIP)
  // ===================================================================

  /**
   * Apply custom overrides for VIP clients
   */
  applyCustomOverride(
    subscriptionId: number,
    request: CustomOverrideRequest
  ): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<ClinicSubscriptionResponse>>(
      `${this.apiUrl}/${subscriptionId}/override`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success(response.message || 'تم تطبيق التخصيصات بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في تطبيق التخصيصات');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  // ===================================================================
  // READ OPERATIONS
  // ===================================================================

  /**
   * Get all subscriptions with filtering and pagination (NEW)
   * For SYSTEM_ADMIN to view all clinic subscriptions
   */
  getAllSubscriptions(
    criteria?: SubscriptionSearchCriteria,
    page: number = 0,
    size: number = 20,
    sortBy: string = 'createdAt',
    sortDirection: 'ASC' | 'DESC' = 'DESC'
  ): Observable<ApiResponse<PageResponse<ClinicSubscriptionResponse>>> {
    this.loading.set(true);

    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    // Add search criteria if provided
    if (criteria) {
      if (criteria.searchTerm) {
        params = params.set('searchTerm', criteria.searchTerm);
      }
      if (criteria.status) {
        params = params.set('status', criteria.status);
      }
      if (criteria.planId) {
        params = params.set('planId', criteria.planId.toString());
      }
      if (criteria.expiringInDays !== undefined) {
        params = params.set('expiringInDays', criteria.expiringInDays.toString());
      }
      if (criteria.isOnTrial !== undefined) {
        params = params.set('isOnTrial', criteria.isOnTrial.toString());
      }
      if (criteria.autoRenew !== undefined) {
        params = params.set('autoRenew', criteria.autoRenew.toString());
      }
    }

    return this.http.get<ApiResponse<PageResponse<ClinicSubscriptionResponse>>>(
      this.apiUrl,
      { params }
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          // Update state with the content from the page
          this.subscriptions.set(response.data.content);
          this.subscriptionsSubject.next(response.data.content);
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في جلب الاشتراكات');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get subscription statistics (NEW)
   * Dashboard metrics for SYSTEM_ADMIN
   */
  getStatistics(): Observable<ApiResponse<SubscriptionStatisticsResponse>> {
    this.loading.set(true);
    return this.http.get<ApiResponse<SubscriptionStatisticsResponse>>(
      `${this.apiUrl}/statistics`
    ).pipe(
      tap(response => {
        if (response.success) {
          this.statistics.set(response.data!);
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في جلب الإحصائيات');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get subscription for a specific clinic
   */
  getSubscriptionByClinicId(clinicId: number, silent: boolean = false): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);

    const context = silent ? new HttpContext().set(SILENT_HTTP, true) : new HttpContext();

    return this.http.get<ApiResponse<ClinicSubscriptionResponse>>(
      `${this.apiUrl}/clinic/${clinicId}`,
      { context }
    ).pipe(
      tap(response => {
        if (response.success) {
          this.selectedSubscription.set(response.data!);
        }
        this.loading.set(false);
      }),
      catchError(error => {
        if (!silent) {
          const customErrormessage = error.error?.message.includes("No active subscription found for clinic")
            ? "لا يوجد اشتراك نشط لهذه العيادة"
            : error.error?.message;
          this.notificationService.error(customErrormessage || 'فشل في جلب تفاصيل الاشتراك');
        }
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get subscription by ID
   */
  getSubscriptionById(subscriptionId: number): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    this.loading.set(true);
    return this.http.get<ApiResponse<ClinicSubscriptionResponse>>(`${this.apiUrl}/${subscriptionId}`).pipe(
      tap(response => {
        if (response.success) {
          this.selectedSubscription.set(response.data!);
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في جلب تفاصيل الاشتراك');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get subscriptions expiring soon
   */
  getExpiringSoon(daysAhead: number = 30): Observable<ApiResponse<ClinicSubscriptionResponse[]>> {
    const params = new HttpParams().set('daysAhead', daysAhead.toString());
    return this.http.get<ApiResponse<ClinicSubscriptionResponse[]>>(`${this.apiUrl}/expiring`, { params }).pipe(
      tap(response => {
        if (response.success) {
          this.subscriptions.set(response.data!);
          this.subscriptionsSubject.next(response.data!);
        }
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في جلب الاشتراكات المنتهية');
        return throwError(() => error);
      })
    );
  }

  /**
   * Get trials ending soon
   */
  getTrialsEndingSoon(daysAhead: number = 7): Observable<ApiResponse<ClinicSubscriptionResponse[]>> {
    const params = new HttpParams().set('daysAhead', daysAhead.toString());
    return this.http.get<ApiResponse<ClinicSubscriptionResponse[]>>(`${this.apiUrl}/trials-ending`, { params }).pipe(
      tap(response => {
        if (response.success) {
          this.subscriptions.set(response.data!);
          this.subscriptionsSubject.next(response.data!);
        }
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في جلب الفترات التجريبية');
        return throwError(() => error);
      })
    );
  }

  // ===================================================================
  // HISTORY
  // ===================================================================

  /**
   * Get subscription history
   */
  getSubscriptionHistory(subscriptionId: number): Observable<ApiResponse<SubscriptionHistoryResponse[]>> {
    return this.http.get<ApiResponse<SubscriptionHistoryResponse[]>>(
      `${this.apiUrl}/${subscriptionId}/history`
    ).pipe(
      tap(response => {
        if (response.success) {
          this.history.set(response.data!);
        }
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في جلب سجل الاشتراك');
        return throwError(() => error);
      })
    );
  }

  /**
   * Get subscription history by clinic (paginated)
   */
  getHistoryByClinic(
    clinicId: number,
    page: number = 0,
    size: number = 20
  ): Observable<ApiResponse<PageResponse<SubscriptionHistoryResponse>>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<ApiResponse<PageResponse<SubscriptionHistoryResponse>>>(
      `${this.apiUrl}/clinic/${clinicId}/history`,
      { params }
    ).pipe(
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في جلب سجل الاشتراكات');
        return throwError(() => error);
      })
    );
  }

  // ===================================================================
  // HELPERS & UTILITIES
  // ===================================================================

  /**
   * Clear selected subscription from state
   */
  clearSelectedSubscription(): void {
    this.selectedSubscription.set(null);
  }

  /**
   * Clear history from state
   */
  clearHistory(): void {
    this.history.set([]);
  }

  /**
   * Clear statistics from state
   */
  clearStatistics(): void {
    this.statistics.set(null);
  }

  /**
   * Clear all subscriptions from state
   */
  clearSubscriptions(): void {
    this.subscriptions.set([]);
    this.subscriptionsSubject.next([]);
  }

  /**
   * Check if subscription is expiring soon (within 30 days)
   */
  isExpiringSoon(subscription: ClinicSubscriptionResponse): boolean {
    return (subscription.daysUntilExpiration !== undefined && subscription.daysUntilExpiration <= 30);
  }

  /**
   * Check if subscription is on trial
   */
  isOnTrial(subscription: ClinicSubscriptionResponse): boolean {
    return subscription.isInTrial!;
  }

  /**
   * Check if subscription has custom overrides
   */
  // hasCustomOverrides(subscription: ClinicSubscriptionResponse): boolean {
  //   return subscription.hasCustomOverrides;
  // }

  /**
   * Get status color for UI display
   */
  getStatusColor(status: SubscriptionStatus): string {
    const colorMap: Record<SubscriptionStatus, string> = {
      [SubscriptionStatus.ACTIVE]: 'success',
      [SubscriptionStatus.TRIAL]: 'primary',
      [SubscriptionStatus.EXPIRED]: 'danger',
      [SubscriptionStatus.CANCELLED]: 'medium',
      [SubscriptionStatus.SUSPENDED]: 'warning',
      [SubscriptionStatus.PENDING]: 'secondary'
    };
    return colorMap[status] || 'medium';
  }

  /**
   * Format remaining days text
   */
  formatRemainingDays(days?: number): string {
    if (days === undefined || days === null) return 'غير محدد';
    if (days < 0) return 'منتهي';
    if (days === 0) return 'ينتهي اليوم';
    if (days === 1) return 'يوم واحد متبقي';
    if (days === 2) return 'يومان متبقيان';
    if (days <= 10) return `${days} أيام متبقية`;
    return `${days} يوم متبقي`;
  }

  // ===================================================================
  // SUBSCRIPTION REQUEST OPERATIONS - ADMIN
  // ===================================================================

  /**
 * ADMIN: Create subscription request (unified for all types)
 * Supports NEW_SUBSCRIPTION, UPGRADE, DOWNGRADE, and RENEW
 */
  createSubscriptionRequest(request: SubscriptionRequestRequest): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/requests`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success('تم إرسال طلب الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        // this.notificationService.error(error.error?.message || 'حدث خطأ أثناء إرسال الطلب');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * ADMIN: Request upgrade
   */
  // DEPRECATED: Keep for backward compatibility
  /**
   * @deprecated Use createSubscriptionRequest with requestType: 'UPGRADE'
   */
  requestUpgrade(request: SubscriptionRequestRequest): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/request-upgrade`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success('تم إرسال طلب الترقية بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في إرسال طلب الترقية');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * ADMIN: Request downgrade
   */
  /**
 * @deprecated Use createSubscriptionRequest with requestType: 'DOWNGRADE'
 */
  requestDowngrade(request: SubscriptionRequestRequest): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/request-downgrade`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success('تم إرسال طلب التخفيض بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في إرسال طلب التخفيض');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * ADMIN: Request renewal
   */
  /**
 * @deprecated Use createSubscriptionRequest with requestType: 'RENEW'
 */
  requestRenew(request: SubscriptionRequestRequest): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/request-renew`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success('تم إرسال طلب التجديد بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في إرسال طلب التجديد');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * ADMIN: Get my requests
   */
  getMyRequests(): Observable<ApiResponse<SubscriptionRequest[]>> {
    this.loading.set(true);
    return this.http.get<ApiResponse<SubscriptionRequest[]>>(
      `${this.apiUrl}/my-requests`
    ).pipe(
      tap(() => this.loading.set(false)),
      catchError(error => {
        this.notificationService.error('فشل في جلب الطلبات');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  // ===================================================================
  // SUBSCRIPTION REQUEST OPERATIONS - SYSTEM_ADMIN
  // ===================================================================

  /**
   * SYSTEM_ADMIN: Get all requests with filters
   */
  getAllRequests(
    status?: SubscriptionRequestStatus,
    requestType?: SubscriptionRequestType,
    page: number = 0,
    size: number = 20
  ): Observable<ApiResponse<PageResponse<SubscriptionRequest>>> {
    this.loading.set(true);

    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (status) {
      params = params.set('status', status);
    }
    if (requestType) {
      params = params.set('requestType', requestType);
    }

    return this.http.get<ApiResponse<PageResponse<SubscriptionRequest>>>(
      `${this.apiUrl}/requests`,
      { params }
    ).pipe(
      tap(() => this.loading.set(false)),
      catchError(error => {
        this.notificationService.error('فشل في جلب الطلبات');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * SYSTEM_ADMIN: Get request details
   */
  getRequestById(id: number): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);
    return this.http.get<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/requests/${id}`
    ).pipe(
      tap(() => this.loading.set(false)),
      catchError(error => {
        this.notificationService.error('فشل في جلب تفاصيل الطلب');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * SYSTEM_ADMIN: Move to review
   */
  moveToReview(id: number, request: ReviewSubscriptionRequestRequest): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/requests/${id}/review`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success('تم نقل الطلب للمراجعة بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error('فشل في نقل الطلب للمراجعة');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * SYSTEM_ADMIN: Approve request
   */
  approveRequest(id: number, request: ApproveSubscriptionRequestRequest): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/requests/${id}/approve`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success('تم الموافقة على الطلب وتفعيل الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error('فشل في الموافقة على الطلب');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * SYSTEM_ADMIN: Reject request
   */
  rejectRequest(id: number, request: RejectSubscriptionRequestRequest): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);
    return this.http.post<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/requests/${id}/reject`,
      request
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success('تم رفض الطلب بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error('فشل في رفض الطلب');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
 * Toggle a custom quota override on/off
 */
  toggleQuotaOverride(subscriptionId: number, quotaType: string, enabled: boolean): Observable<ApiResponse<ClinicSubscriptionResponse>> {
    return this.http.patch<ApiResponse<ClinicSubscriptionResponse>>(
      `${this.apiUrl}/${subscriptionId}/override/quota/${quotaType}/toggle?enabled=${enabled}`,
      {}
    );
  }

  /**
 * ADMIN: Cancel own subscription request
 * Can only cancel INACTIVE or UNDER_REVIEW requests
 */
  cancelOwnRequest(requestId: number, reason?: string): Observable<ApiResponse<SubscriptionRequest>> {
    this.loading.set(true);

    const body = reason ? { reason } : {};

    return this.http.delete<ApiResponse<SubscriptionRequest>>(
      `${this.apiUrl}/requests/${requestId}/cancel`,
      { body }
    ).pipe(
      tap(response => {
        if (response.success) {
          this.notificationService.success('تم إلغاء طلب الاشتراك بنجاح');
        }
        this.loading.set(false);
      }),
      catchError(error => {
        this.notificationService.error(error.error?.message || 'فشل في إلغاء الطلب');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

}
